import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { LogOut, Menu, User, Settings, Sparkles, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { NavLink } from "@/components/NavLink";
import { NotificationDropdown } from "@/components/NotificationDropdown";
import { KeyboardShortcutsOverlay } from "@/components/KeyboardShortcutsOverlay";
import { UserMenu } from "./UserMenu";
import { useUserRole } from "@/hooks/useUserRole";
import { getNavItemsForRole, APP_ROLE_LABELS } from "@/lib/permissions";


export const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [userEmail, setUserEmail] = useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: userRole } = useUserRole();

  // Role-filtered navigation items
  const navItems = useMemo(() => getNavItemsForRole(userRole ?? null), [userRole]);
  const mainNavItems = useMemo(() => navItems.filter(i => i.section === 'main' || (i.section === 'ops' && i.path === '/operations')), [navItems]);
  const aiNavItems = useMemo(() => navItems.filter(i => i.section === 'ai'), [navItems]);

  useEffect(() => {
    const getUserEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    getUserEmail();
  }, []);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: t('common.error'),
        description: t('common.signOutError'),
        variant: "destructive",
      });
    } else {
      queryClient.clear();
      setMobileMenuOpen(false);
      navigate("/auth");
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const profileItem = { path: "/profile", label: "navigation.profile", icon: User };

  // Hide bottom nav on immersive pages (messages, dashboard)
  const hideBottomNav = 
    location.pathname.startsWith("/messages") || 
    location.pathname === "/dashboard";

  return (
    <>
      <KeyboardShortcutsOverlay />
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between gap-4">
          {/* Mobile: Hamburger left, Logo centered, Actions right */}
          <div className="md:hidden flex items-center justify-between w-full">
            {/* Left: Hamburger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 touch-manipulation">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="font-playfair text-xl">
                    Stay Properties
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                  {/* User Info */}
                  {userEmail && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {userRole ? t(APP_ROLE_LABELS[userRole]) : t('common.account')}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Navigation Links */}
                  <nav className="space-y-1">
                    {mainNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path ||
                        (item.path !== "/dashboard" && location.pathname.startsWith(item.path));

                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors touch-manipulation ${
                            isActive
                              ? "bg-accent text-accent-foreground font-medium"
                              : "hover:bg-accent/50"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          {t(item.label)}
                        </button>
                      );
                    })}
                  </nav>

                  {/* AI Intelligence Section - Collapsible (hidden if no AI items for this role) */}
                  {aiNavItems.length > 0 && (<>
                  <Separator />

                  <Collapsible>
                    <CollapsibleTrigger className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm hover:bg-accent/50 transition-colors touch-manipulation">
                      <div className="flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t('navigation.aiIntelligence')}</span>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-6 space-y-1 mt-1">
                      {aiNavItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <button
                            key={item.path}
                            onClick={() => handleNavigation(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors touch-manipulation ${
                              isActive
                                ? "bg-accent text-accent-foreground font-medium"
                                : "hover:bg-accent/50"
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {t(item.label)}
                          </button>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                  </>)}

                  <Separator />
                  <button
                    onClick={() => handleNavigation(profileItem.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors touch-manipulation ${
                      location.pathname === profileItem.path
                        ? "bg-accent text-accent-foreground font-medium"
                        : "hover:bg-accent/50"
                    }`}
                  >
                    <User className="h-5 w-5" />
                    {t(profileItem.label)}
                  </button>

                  <Separator />

                  {/* Settings Section */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm font-medium">{t('common.settings')}</span>
                    </div>

                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('common.theme')}</span>
                      <ThemeToggle />
                    </div>
                  </div>

                  <Separator />

                  {/* Sign Out */}
                  <Button
                    variant="ghost"
                    onClick={handleSignOut}
                    className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="h-5 w-5" />
                    {t('common.signOut')}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Center: Logo */}
            <button 
              onClick={() => navigate('/dashboard')}
              className="font-playfair text-lg font-bold hover:opacity-80 transition-opacity absolute left-1/2 -translate-x-1/2"
            >
              Stay Properties
            </button>

            {/* Right: Notifications */}
            <NotificationDropdown />
          </div>

          {/* Desktop/Tablet: Logo left */}
          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="font-playfair text-lg font-bold hover:opacity-80 transition-opacity"
            >
              Stay Properties
            </button>
          </div>

          {/* Desktop: Centered Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5 absolute left-1/2 transform -translate-x-1/2">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/dashboard"}
                  className="px-3 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors flex items-center gap-1.5"
                  activeClassName="bg-accent text-accent-foreground font-medium"
                >
                  <Icon className="h-4 w-4" />
                  {t(item.label)}
                </NavLink>
              );
            })}
            
            {/* AI Dropdown Menu — hidden if role has no AI items */}
            {aiNavItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 px-3 py-1.5 h-auto text-sm">
                  <Sparkles className="h-4 w-4" />
                  {t('common.ai')}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {aiNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {t(item.label)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </nav>

          {/* Tablet: Centered Compact nav with icons only */}
          <nav className="hidden min-[900px]:flex xl:hidden items-center gap-0.5 absolute left-1/2 transform -translate-x-1/2">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/dashboard"}
                  className="px-2 py-2 text-sm rounded-md hover:bg-accent/50 transition-colors"
                  activeClassName="bg-accent text-accent-foreground font-medium"
                  title={t(item.label)}
                >
                  <Icon className="h-5 w-5" />
                </NavLink>
              );
            })}
            
            {/* Tablet: AI Dropdown (icon only) — hidden if role has no AI items */}
            {aiNavItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Sparkles className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {aiNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {t(item.label)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </nav>

          {/* Desktop & Tablet Actions */}
          <div className="hidden md:flex items-center gap-1">
            <NotificationDropdown />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>

        {/* Mobile Bottom Nav - Hidden on immersive pages, max 5 items for touch targets */}
        {!hideBottomNav && (
          <nav className="md:hidden border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-1 py-1 pb-safe flex justify-around">
              {mainNavItems.slice(0, 5).map((item) => {
                const Icon = item.icon;
                const translatedLabel = t(item.label);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === "/dashboard"}
                    className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                    activeClassName="bg-accent text-accent-foreground font-medium"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{translatedLabel.length > 10 ? translatedLabel.slice(0, 8) + '.' : translatedLabel}</span>
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
      </header>
    </>
  );
};
